import React, { useState, useEffect, useMemo } from "react"
import styled, { css } from "styled-components"
import PropTypes from "prop-types"
import InputCheckbox from "@src/components/input-checkbox"
import Link from "@src/components/core-link"
import Button from "@src/components/core-button"
import { mapTreeToList, getAncestorList } from "@src/utils/trees"
const InputListWrapper = styled.div``
const InputWrapper = styled.div`
  & > .tree-node {
    display: none;
  }
  &.tree-node-open {
    > .tree-node {
      display: block;
    }
  }
  ${props =>
    !props.$condensed &&
    props.$depth &&
    css`
      padding-left: 15px;
    `}
`
const BreadcrumbWrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  margin: -5px 0 5px;
`
const Breadcrumb = styled.div`
  flex: 0 0 auto;
  margin: 5px 0;
  &:nth-child(n + 3) {
    &::before {
      content: " - ";
      display: inline-block;
      width: 20px;
      text-align: center;
    }
  }
`
const ResetButton = styled(Button)`
  padding: 0;
  margin: 5px 10px 5px 0;
`

// recursive utility to uncheck all descendents of given parent in a CheckboxesTree's state
const uncheckTreeChildren = (parent, tree) => {
  if (tree.checkedChildren[parent]) {
    tree.value = tree.value.filter(
      check => !tree.checkedChildren[parent].includes(check),
    )
    tree.checked = tree.checked.filter(
      check => !tree.checkedChildren[parent].includes(check),
    )
    for (const child of tree.checkedChildren[parent]) {
      uncheckTreeChildren(child, tree)
    }
    delete tree.checkedChildren[parent]
  }
  return tree
}

const getCondensedNewTreeWithItemAdded = (
  prevTree,
  { itemValue, parents, hasChildren },
) => {
  const parent =
    parents && parents.length ? parents[parents.length - 1] : undefined
  if (hasChildren) {
    // has children
    const checkedChildren = {}
    if (parent) {
      for (const checkParent of parents) {
        if (checkParent === parent) {
          checkedChildren[checkParent] = [itemValue]
        } else if (prevTree.checkedChildren[checkParent]) {
          checkedChildren[checkParent] = prevTree.checkedChildren[
            checkParent
          ].filter(check => check === itemValue || parents.includes(check))
        }
      }
    }
    return {
      checked: parents ? [...parents, itemValue] : [itemValue],
      checkedChildren,
      value: [itemValue],
    }
  } else if (parent) {
    // no children, has parent
    const checkedSiblings = prevTree.checkedChildren[parent] || []
    const newTree = {
      checked: parents
        ? [...parents, ...checkedSiblings, itemValue]
        : [itemValue],
      checkedChildren: {},
      value: parents ? [...checkedSiblings, itemValue] : [itemValue],
    }
    for (const checkParent of parents) {
      if (checkParent === parent) {
        newTree.checkedChildren[checkParent] = [...checkedSiblings, itemValue]
      } else if (prevTree.checkedChildren[checkParent]) {
        newTree.checkedChildren[checkParent] = prevTree.checkedChildren[
          checkParent
        ].filter(
          check => check === itemValue || newTree.checked.includes(check),
        )
      }
    }
    return newTree
  } else {
    // no children, no parent (root item)
    const newTree = {
      checked: [...prevTree.checked, itemValue],
      checkedChildren: { ...prevTree.checkedChildren },
      value: [...prevTree.value, itemValue],
    }
    // uncheck any non-root items
    for (const check of newTree.checked) {
      uncheckTreeChildren(check, newTree)
    }
    // make sure all checked items are in the value list
    for (const check of newTree.checked) {
      if (!newTree.value.includes(check)) {
        newTree.value.push(check)
      }
    }
    return newTree
  }
}
const getCondensedNewTreeWithItemRemoved = (
  prevTree,
  { itemValue, parents },
) => {
  const parent =
    parents && parents.length ? parents[parents.length - 1] : undefined
  // newTree with itemValue removed from  checked and value lists
  const newTree = {
    checked: [...prevTree.checked.filter(check => check !== itemValue)],
    checkedChildren: { ...prevTree.checkedChildren },
    value: [...prevTree.value.filter(check => check !== itemValue)],
  }
  // uncheck any of the items children
  uncheckTreeChildren(itemValue, newTree)
  if (parent) {
    if (newTree.checkedChildren[parent]) {
      // remove the item from its parent's checkedChildren list
      newTree.checkedChildren[parent] = newTree.checkedChildren[parent].filter(
        check => check !== itemValue,
      )

      // remove the parent's checkedChildren list if it's empty
      if (!newTree.checkedChildren[parent].length) {
        delete newTree.checkedChildren[parent]
      }
    }

    // in case this was the last child of parent to be unchecked
    if (!newTree.checkedChildren[parent]) {
      // ensure the parent is marked as checked
      if (!newTree.checked.includes(parent)) {
        newTree.checked.push(parent)
      }
      // include the parent in the tree value
      if (!newTree.value.includes(parent)) {
        newTree.value.push(parent)
      }
    }
  }
  return newTree
}

const getNewTreeWithItemAdded = (prevTree, { itemValue, parents }) => {
  const parent =
    parents && parents.length ? parents[parents.length - 1] : undefined

  // clone previous tree
  const newTree = {
    checked: [...prevTree.checked],
    checkedChildren: { ...prevTree.checkedChildren },
    value: [...prevTree.value],
  }

  // mark the value as checked
  if (!newTree.checked.includes(itemValue)) {
    newTree.checked.push(itemValue)
  }

  // update parent's checkedChildren
  if (parent) {
    if (!newTree.checkedChildren[parent]) {
      newTree.checkedChildren[parent] = []
    }
    if (!newTree.checkedChildren[parent].includes(itemValue)) {
      newTree.checkedChildren[parent].push(itemValue)
    }

    //  remove parent from tree.value (it will be replaced with itemValue)
    if (newTree.value.includes(parent)) {
      newTree.value = newTree.value.filter(check => check !== parent)
    }
  }

  // add the item's value into tree.value
  if (!newTree.value.includes(itemValue)) {
    newTree.value.push(itemValue)
  }

  return newTree
}
const getNewTreeWithItemRemoved = (prevTree, { itemValue, parents }) => {
  const parent =
    parents && parents.length ? parents[parents.length - 1] : undefined

  // clone previous tree, filtering itemValue out of checked and value
  const newTree = {
    checked: prevTree.checked.filter(check => check !== itemValue),
    checkedChildren: { ...prevTree.checkedChildren },
    value: prevTree.value.filter(check => check !== itemValue),
  }

  // remove item's checkedChildren and all their checked descendents
  uncheckTreeChildren(itemValue, newTree)

  // update parent's checkedChildren
  if (parent && newTree.checkedChildren[parent]) {
    newTree.checkedChildren[parent] = newTree.checkedChildren[parent].filter(
      check => check !== itemValue,
    )
    // if it was the last of its siblings to be checked
    if (!newTree.checkedChildren[parent].length) {
      // clean up the parent's checkedChildren entry
      delete newTree.checkedChildren[parent]

      // replace with its parent's value
      if (newTree.checked.includes(parent) && !newTree.value.includes(parent)) {
        newTree.value.push(parent)
      }
    }
  }

  return newTree
}

// InputCheckboxesTree - the default component to export
const InputCheckboxesTree = ({
  id,
  name,
  value: defaultValue,
  items,
  getItemText = item => item.text,
  getItemValue = item => item.value,
  getItemChildren = item => item.childItems,
  condensed,
  onChange,
  variant,
}) => {
  // properties of tree interact with each other when updated, so group them as one state object
  const [tree, setTree] = useState({
    checked: defaultValue || [],
    checkedChildren: {},
    value: defaultValue || [],
  })
  const [breadcrumbs, setBreadcrumbs] = useState([])

  const itemsFlat = useMemo(() => mapTreeToList(items), [items])

  // effect to handle defaultValue changing
  useEffect(() => {
    let newTree = { checked: [], checkedChildren: {}, value: [] }
    const newBreadcrumbs = []
    if (defaultValue && defaultValue.length) {
      // traverse through selected values to build new state values
      // new state values will match the values set when user clicks down the tree manually
      for (const selectValue of defaultValue) {
        const ancestorIds = getAncestorList(selectValue, itemsFlat)
        if (ancestorIds && ancestorIds.length) {
          let lastAncestorId = null
          for (const ancestorId of ancestorIds) {
            if (!newTree.checked.includes(ancestorId)) {
              newTree.checked.push(ancestorId)
            }
            if (!newTree.value.includes(ancestorId)) {
              newTree.value.push(ancestorId)
            }
            if (condensed && !newBreadcrumbs.includes(ancestorId)) {
              const ancestorItem = itemsFlat.find(
                check => check.id === ancestorId,
              )
              if (
                ancestorItem &&
                ancestorItem.items &&
                ancestorItem.items.length
              ) {
                newBreadcrumbs.push(ancestorId)
              }
            }
            if (lastAncestorId !== null) {
              if (!newTree.checkedChildren[lastAncestorId]) {
                newTree.checkedChildren[lastAncestorId] = []
              }
              if (
                !newTree.checkedChildren[lastAncestorId].includes(ancestorId)
              ) {
                newTree.checkedChildren[lastAncestorId].push(ancestorId)
              }
            }
            lastAncestorId = ancestorId
          }
        }
      }
    }
    // items with checked children should not be included in the value
    newTree.value = newTree.value.filter(
      check =>
        !newTree.checkedChildren[check] ||
        !newTree.checkedChildren[check].length,
    )

    // apply the new state
    setBreadcrumbs(newBreadcrumbs)
    setTree(newTree)
  }, [defaultValue, itemsFlat, condensed, setBreadcrumbs, setTree])

  const updateTree = (tree, breadcrumbs) => {
    setTree(tree)
    if (breadcrumbs) {
      setBreadcrumbs(breadcrumbs)
    }
    if (onChange) {
      // tree.value is exposed as the CheckboxTree's value
      onChange(tree.value)
    }
  }
  const resetTree = () => {
    updateTree(
      {
        checked: [],
        checkedChildren: {},
        value: [],
      },
      [],
    )
  }
  const handleChangeCondensed = ({
    item: itemValue,
    parents,
    hasChildren,
    removed,
  }) => {
    if (removed) {
      const newBreadcrumbs = parents ? [...parents] : []
      const newTree = getCondensedNewTreeWithItemRemoved(tree, {
        itemValue,
        parents,
      })
      updateTree(newTree, newBreadcrumbs)
    } else {
      const newBreadcrumbs = parents ? [...parents] : []
      if (hasChildren) {
        newBreadcrumbs.push(itemValue)
      }
      const newTree = getCondensedNewTreeWithItemAdded(tree, {
        itemValue,
        parents,
        hasChildren,
      })
      updateTree(newTree, newBreadcrumbs)
    }
  }
  const handleChange = ({ item: itemValue, parents, hasChildren, removed }) => {
    if (condensed) {
      return handleChangeCondensed({
        item: itemValue,
        parents,
        hasChildren,
        removed,
      })
    }
    if (removed) {
      const newTree = getNewTreeWithItemRemoved(tree, { itemValue, parents })
      updateTree(newTree)
    } else {
      const newTree = getNewTreeWithItemAdded(tree, { itemValue, parents })
      updateTree(newTree)
    }
  }

  const getItemCheckboxes = (item, index, parents) => {
    const depth = parents.length
    const itemText = getItemText(item)
    const itemValue = getItemValue(item)
    const itemChildren = getItemChildren(item)
    if (itemText && itemValue) {
      const checked = tree.checked.includes(itemValue)
      const parent = parents.length ? parents[parents.length - 1] : undefined
      const lastBreadcrumb = breadcrumbs.length
        ? breadcrumbs[breadcrumbs.length - 1]
        : undefined
      const showInput =
        (!condensed && (!parent || tree.checked.includes(parent))) ||
        (condensed &&
          ((!parent && !breadcrumbs.length) ||
            (parent && parent === lastBreadcrumb)))
      const showChildren = checked && itemChildren && itemChildren.length
      return (
        <InputWrapper
          $depth={depth}
          $condensed={condensed}
          className={`tree-node${checked ? " tree-node-open" : ""}`}
          key={`${itemValue}${index}`}
        >
          {showInput ? (
            <InputCheckbox
              id={`${id}-${index}`}
              name={`${name || id}[]`}
              label={itemText}
              valueTrue={itemValue}
              checked={checked}
              onChange={value => {
                const event = {
                  item: itemValue,
                  parents,
                  hasChildren: itemChildren && itemChildren.length,
                  removed: !value,
                }
                return condensed
                  ? handleChangeCondensed(event)
                  : handleChange(event)
              }}
              variant={variant}
            />
          ) : null}
          {showChildren
            ? itemChildren.map((childItem, childIndex) =>
                getItemCheckboxes(childItem, `${index}-${childIndex}`, [
                  ...parents,
                  itemValue,
                ]),
              )
            : null}
        </InputWrapper>
      )
    }
  }

  // getBreadcrumbItems - get the breadcrumb trail as a list of items from the tree
  const getBreadcrumbItems = trail => {
    const breadcrumbItems = []
    let stepItems = items
    for (const step of trail) {
      if (stepItems && stepItems.length) {
        const stepItem = stepItems.find(check => getItemValue(check) === step)
        if (stepItem) {
          const itemText = getItemText(stepItem)
          const itemValue = getItemValue(stepItem)
          const itemChildren = getItemChildren(stepItem)
          breadcrumbItems.push({
            text: itemText,
            value: itemValue,
            hasChildren: itemChildren && itemChildren.length,
          })
          stepItems = itemChildren
        } else {
          break
        }
      } else {
        break
      }
    }
    return breadcrumbItems
  }
  // renderBreadcrumbs() - render breadcrumb list
  const renderBreadcrumbs = breadcrumbItems => {
    const parents = []
    const BreadCrumbs = breadcrumbItems.map(item => {
      const itemParents = [...parents]
      parents.push(item.value)
      return (
        <Breadcrumb key={`breadcrumb-${item.value}`}>
          <Link
            onClick={event => {
              event.preventDefault()
              const treeEvent = {
                item: item.value,
                parents: itemParents,
                hasChildren: item.hasChildren,
              }
              return condensed
                ? handleChangeCondensed(treeEvent)
                : handleChange(treeEvent)
            }}
          >
            {item.text}
          </Link>
        </Breadcrumb>
      )
    })
    return (
      <BreadcrumbWrapper>
        {
          <ResetButton
            variant="icon"
            icon="circle-x"
            onClick={event => {
              event.preventDefault()
              resetTree()
            }}
          />
        }
        {BreadCrumbs}
      </BreadcrumbWrapper>
    )
  }

  return (
    <InputListWrapper>
      {condensed && breadcrumbs.length
        ? renderBreadcrumbs(getBreadcrumbItems(breadcrumbs))
        : undefined}
      {items && items.length
        ? items.map((item, index) => getItemCheckboxes(item, index, []))
        : undefined}
    </InputListWrapper>
  )
}
InputCheckboxesTree.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.any,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.any,
    }),
  ),
  getItemText: PropTypes.func,
  getItemValue: PropTypes.func,
  getItemChildren: PropTypes.func,
  condensed: PropTypes.bool,
  onChange: PropTypes.func,
  variant: PropTypes.oneOf(["blue", "yellow"]),
}

export default InputCheckboxesTree
